import {
  put, call, select, delay,
} from 'lib/redux';
import * as types from 'constants/ActionTypes';
import initParametersService from 'services/getInitParameters.service';
import billPayerInfoService from 'services/billPayerInfo.service';
import getUserProductsService from 'services/getUserProducts.service';
import controlStatesService from 'services/controlStates.service';
import getOrderPreferencesService from 'services/getOrderPreferences.service';
import getCompanyInformationService from 'services/getCompanyInformation.service';
import { isObjectEmpty } from 'utils/app.utils';
import getOrgDescendantsService from 'services/getOrgDescendants.service';
import getAccountSummaryService from 'services/getAccountSummary.service';
import getuserEntitledAppsService from 'services/getUserEntitledApps.service';
import getEntitledThirdPartyAppsService from 'services/getEntitledThirdPartyApps.service'; // #genai
import { PRODUCT_IDS } from 'constants/AppConstants';
import { isEntitledForGlobalSearchSelector, isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils'; // #genai
import isEmpty from 'lodash/isEmpty';
import { logError } from 'utils/logger.utils';
import getSubscriptionDashboardDataService from 'services/getSubscriptionDashboardData.service';
import getSubscriptionDashboardV2Service from 'services/getSubscriptionDashboardV2.service'; // #genai
import getUserProfileService from 'services/getUserProfile.service';
import getConsumptionService from 'services/getConsumption.service';
import mockNewSubscriptionDashboardData from 'constants/mockNewSubscriptionDashboardData';
import { parseDateToISOString } from 'utils/date.utils';

function* pollForData({ loadingSelector, dataSelector, errorSelector }) {
  const apiLoading = yield select(loadingSelector);
  if (apiLoading) {
    while (true) {
      const { data, error } = yield select((state) => ({
        data: dataSelector(state),
        error: errorSelector(state),
      }));

      if (error || !isObjectEmpty(data)) {
        break;
      }
      yield delay(200);
    }
  }
}

export function* getBillingInformationSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.billingInformation,
    dataSelector: ({ userData }) => userData.billingInformation,
    errorSelector: ({ userData }) => userData.error.billingInformation,
  });
  try {
    const billingInformation = yield select(({ userData }) => userData?.billingInformation);
    if (isUpdated || isObjectEmpty(billingInformation)) {
      const response = yield call(billPayerInfoService);
      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      yield put({ type: types.GET_BILLING_INFORMATION_SUCCESS, billingInformation: response.json });
    }
  } catch (error) {
    yield put({ type: types.GET_BILLING_INFORMATION_ERROR, error: error.message });
  }
}

export function* getCompanyInfoSaga() {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.companyInformation,
    dataSelector: ({ userData }) => userData.companyInformation,
    errorSelector: ({ userData }) => userData.error.companyInformation,
  });
  const { companyInformation } = yield select(({ userData }) => (userData));
  if (Object.keys(companyInformation).length === 0) {
    try {
      yield put({ type: types.COMPANY_INFO_LOADING });
      const companyInfoResponse = yield call(getCompanyInformationService);
      if (!companyInfoResponse.ok) {
        const error = new Error(companyInfoResponse.json.errorDescription);
        error.errorCode = companyInfoResponse.json.errorCode;
        throw error;
      }
      yield put({ type: types.COMPANY_INFO_SUCCESS, companyInformation: companyInfoResponse.json });
    } catch (error) {
      logError('Error in getCompanyInfoSaga', {
        error: error.message,
        errorCode: error.errorCode,
      });
      yield put({ type: types.COMPANY_INFO_ERROR });
    }
  }
}

export function* getInitParametersSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.initParameters,
    dataSelector: ({ userData }) => userData.initParameters,
    errorSelector: ({ userData }) => userData.error.initParameters,
  });

  const initParameters = yield select(({ userData }) => userData?.initParameters);
  if (isUpdated || isObjectEmpty(initParameters)) {
    yield put({ type: types.INIT_PARAMETERS_LOADING });
    try {
      const response = yield call(initParametersService);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }

      yield put({ type: types.INIT_PARAMETERS_SUCCESS, initParameters: response.json });
    } catch (error) {
      logError('Error in getInitParametersSaga', {
        error: error.message,
        errorCode: error.errorCode,
        isUpdated,
      });
      yield put({ type: types.INIT_PARAMETERS_ERROR, error: error.message });
    }
  }
}

export function* getUserProductsSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.userProducts,
    dataSelector: ({ userData }) => userData.userProducts,
    errorSelector: ({ userData }) => userData.error.userProducts,
  });
  const fetchedUserProducts = yield select(({ userData }) => userData?.userProducts);
  if (isUpdated || isObjectEmpty(fetchedUserProducts)) {
    try {
      yield put({ type: types.USER_PRODUCTS_LOADING });
      const response = yield call(getUserProductsService);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const userProducts = response.json;
      const isRoofUser = userProducts?.Residential?.PrimaryProducts?.some((product) => product.ProductID === PRODUCT_IDS.Roof);
      yield put({ type: types.USER_PRODUCTS_SUCCESS, isRoofUser, userProducts });
    } catch (error) {
      yield put({ type: types.USER_PRODUCTS_ERROR, error: error.message });
    }
  }
}

// #genai Loads external + internal products for OH/Analytics filters without touching userProducts
export function* getProductCatalogSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.productCatalog,
    dataSelector: ({ userData }) => userData.productCatalog,
    errorSelector: ({ userData }) => userData.error.productCatalog,
  });
  const fetchedProductCatalog = yield select(({ userData }) => userData?.productCatalog);
  if (isUpdated || isObjectEmpty(fetchedProductCatalog)) {
    try {
      yield put({ type: types.PRODUCT_CATALOG_LOADING });
      const response = yield call(getUserProductsService, { includeInternal: true });

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      yield put({ type: types.PRODUCT_CATALOG_SUCCESS, productCatalog: response.json });
    } catch (error) {
      yield put({ type: types.PRODUCT_CATALOG_ERROR, error: error.message });
    }
  }
}

export function* getControlStatesSaga({ moduleName } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.controlStates,
    dataSelector: ({ userData }) => userData.controlStates,
    errorSelector: ({ userData }) => userData.error.controlStates,
  });
  const controlStates = yield select(({ userData }) => userData?.controlStates);
  if (isObjectEmpty(controlStates)) {
    try {
      const userId = (yield select(({ finalizeOrder, userData }) => finalizeOrder.userId || userData.initParameters.customerId));
      const response = yield call(controlStatesService, moduleName, userId);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const controlStatesResponse = response.json;
      yield put({ type: types.CONTROL_STATES_SUCCESS, controlStatesResponse });
    } catch (error) {
      yield put({ type: types.CONTROL_STATES_ERROR, error: error.message });
    }
  }
}

export function* getOrderPreferencesSaga({ isUpdated = false } = {}) {
  yield getInitParametersSaga();
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.orderPreferences,
    dataSelector: ({ userData }) => userData.orderPreferences,
    errorSelector: ({ userData }) => userData.error.orderPreferences,
  });
  const orderPreferences = yield select(({ userData }) => userData?.orderPreferences);
  if (isUpdated || isObjectEmpty(orderPreferences)) {
    try {
      const userId = yield select(({ finalizeOrder, userData }) => finalizeOrder.userId || userData.initParameters.customerId);
      const response = yield call(getOrderPreferencesService, userId);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const orderPreferencesResponse = response.json;
      yield put({ type: types.ORDER_PREFERENCES_SUCCESS, orderPreferencesResponse });
    } catch (error) {
      yield put({ type: types.ORDER_PREFERENCES_ERROR, error: error.message });
    }
  }
}

export function* getOrgDescendantsSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.orgDescendants,
    dataSelector: ({ userData }) => userData.orgDescendants,
    errorSelector: ({ userData }) => userData.error.orgDescendants,
  });
  const orgDescendants = yield select(({ userData }) => userData?.orgDescendants);
  const isEntitledForGlobalSearch = yield select(isEntitledForGlobalSearchSelector);
  if (isUpdated || isObjectEmpty(orgDescendants)) {
    yield put({ type: types.ORG_DESCENDANTS_LOADING });
    yield getInitParametersSaga();
    const { orgNodeId, rootOrgNodeId } = yield select(({ userData }) => userData?.initParameters);
    try {
      const response = yield call(getOrgDescendantsService, isEntitledForGlobalSearch ? rootOrgNodeId : orgNodeId);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const orgDescendantsResponse = response.json;
      yield put({ type: types.ORG_DESCENDANTS_SUCCESS, orgDescendants: orgDescendantsResponse });
    } catch (error) {
      yield put({ type: types.ORG_DESCENDANTS_ERROR, error: error.message });
    }
  }
}

export function* getAccountSummarySaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.accountSummary,
    dataSelector: ({ userData }) => userData.accountSummary,
    errorSelector: ({ userData }) => userData.error.accountSummary,
  });
  const { accountSummary } = yield select(({ userData }) => (userData));

  if (isUpdated || isObjectEmpty(accountSummary)) {
    try {
      yield put({ type: types.LOADING_ACCOUNT_SUMMARY });
      const accountSummaryResponse = yield call(getAccountSummaryService);
      if (!accountSummaryResponse.ok) {
        const error = new Error(accountSummaryResponse.json.errorDescription);
        error.errorCode = accountSummaryResponse.json.errorCode;
        throw error;
      }
      yield put({ type: types.ACCOUNT_SUMMARY_SUCCESS, accountSummary: accountSummaryResponse.json });
    } catch (error) {
      yield put({ type: types.ACCOUNT_SUMMARY_ERROR });
    }
  }
}
export function* getUserEntitledAppsSaga({ isUpdated = false } = {}) {
  const { entitledApps } = yield select(({ userData }) => (userData));
  if (isUpdated || isEmpty(entitledApps)) {
    try {
      yield put({ type: types.USER_ENTITLED_APPS_LOADING });
      const response = yield call(getuserEntitledAppsService);
      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const entitledAppsSortedList = !isEmpty(response.json) && response.json.sort((app1, app2) => app1.order - app2.order);
      yield put({ type: types.USER_ENTITLED_APPS_SUCCESS, entitledApps: entitledAppsSortedList });
    } catch (error) {
      logError('Error in getUserEntitledAppsSaga', {
        error: error.message,
        errorCode: error.errorCode,
        isUpdated,
      });
      yield put({ type: types.USER_ENTITLED_APPS_ERROR });
    }
  }
}

// #genai
export function* getEntitledThirdPartyAppsSaga({ isUpdated = false } = {}) {
  const { entitledThirdPartyApps } = yield select(({ userData }) => (userData));
  if (isUpdated || isEmpty(entitledThirdPartyApps)) {
    try {
      yield put({ type: types.ENTITLED_THIRDPARTY_APPS_LOADING });
      const response = yield call(getEntitledThirdPartyAppsService);
      if (!response.ok) {
        const payload = response.json; // #genai
        const message = (payload && typeof payload === 'object' && payload.errorDescription) // #genai
          || (response?.error?.message) // #genai
          || 'Request failed'; // #genai
        const error = new Error(message); // #genai
        error.errorCode = payload && typeof payload === 'object' ? payload.errorCode : undefined; // #genai
        throw error;
      }
      yield put({
        type: types.ENTITLED_THIRDPARTY_APPS_SUCCESS,
        entitledThirdPartyApps: response.json,
      });
    } catch (error) {
      logError('Error in getEntitledThirdPartyAppsSaga', {
        error: error.message,
        errorCode: error.errorCode,
        isUpdated,
      });
      yield put({ type: types.ENTITLED_THIRDPARTY_APPS_ERROR });
    }
  }
}

export function* getSubscriptionDashboardDataSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.subscriptionDashboard,
    dataSelector: ({ userData }) => userData.subscriptionDashboard,
    errorSelector: ({ userData }) => userData.error.subscriptionDashboard,
  });
  const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector); // #genai
  const { subscriptionDashboard } = yield select(({ userData }) => (userData));
  if (isUpdated || isObjectEmpty(subscriptionDashboard)) {
    try {
      yield put({ type: types.LOADING_SUBSCRIPTION_DASHBOARD });
      yield call(getInitParametersSaga);
      const orgId = yield select(({ userData }) => userData?.initParameters?.orgNodeId);

      const subscriptionDashboardResponse = isSubscriptionDashboardV2Enabled
        ? yield call(getSubscriptionDashboardV2Service, orgId)
        : yield call(getSubscriptionDashboardDataService, orgId);
      if (!subscriptionDashboardResponse.ok) {
        if (isSubscriptionDashboardV2Enabled) { // #genai
          yield put({ type: types.SUBSCRIPTION_DASHBOARD_SUCCESS, subscriptionDashboard: mockNewSubscriptionDashboardData });
          return;
        }
        const error = new Error(subscriptionDashboardResponse.json.errorDescription);
        error.errorCode = subscriptionDashboardResponse.json.errorCode;
        throw error;
      }
      yield put({ type: types.SUBSCRIPTION_DASHBOARD_SUCCESS, subscriptionDashboard: subscriptionDashboardResponse.json });
    } catch (error) {
      logError('Error in getSubscriptionDashboardDataSaga', {
        error: error.message,
        errorCode: error.errorCode,
        isUpdated,
      });
      yield put({ type: types.SUBSCRIPTION_DASHBOARD_ERROR, error: error.message });
    }
  }
}

// #genai
export function* getUserProfileSaga({ isUpdated = false } = {}) {
  yield call(pollForData, {
    loadingSelector: ({ userData }) => userData.loading.userProfile,
    dataSelector: ({ userData }) => userData.userProfile,
    errorSelector: ({ userData }) => userData.error.userProfile,
  });

  const { userProfile } = yield select(({ userData }) => (userData));
  if (isUpdated || isObjectEmpty(userProfile)) {
    try {
      yield put({ type: types.USER_PROFILE_LOADING });
      const response = yield call(getUserProfileService);
      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      yield put({ type: types.USER_PROFILE_SUCCESS, userProfile: response.json });
    } catch (error) {
      logError('Error in getUserProfileSaga', {
        error: error.message,
        errorCode: error.errorCode,
        isUpdated,
      });
      yield put({ type: types.USER_PROFILE_ERROR, error: error.message });
    }
  }
}

// #genai
export function* loadConsumptionDataSaga({ orgId, subscriptionId } = {}) {
  const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector); // #genai
  if (!isSubscriptionDashboardV2Enabled) {
    return;
  }

  try {
    yield put({ type: types.LOAD_CONSUMPTION_DATA_LOADING });
    const orgNodeId = orgId || (yield select(({ userData }) => userData?.initParameters?.orgNodeId));
    if (!orgNodeId) {
      throw new Error('Organization ID is required');
    }
    const subscriptions = yield select(({ userData }) => userData?.subscriptionDashboard?.subscriptions || []);
    const selectedSubscription = subscriptions.find(
      (sub) => String(sub?.subscriptionItemId) === String(subscriptionId),
    );
    const subscriptionStartDate = selectedSubscription?.startDate;
    const subscriptionEndDate = selectedSubscription?.endDate;
    const normalizedStartDate = parseDateToISOString(subscriptionStartDate);
    const normalizedEndDate = parseDateToISOString(subscriptionEndDate);

    // Item-level usage-summary API requires startDate/endDate and validates format/range.
    if (subscriptionId) {
      if (!subscriptionStartDate || !subscriptionEndDate) {
        throw new Error('startDate and endDate are required for subscription usage summary');
      }
      if (!normalizedStartDate || !normalizedEndDate) {
        throw new Error('startDate and endDate must be in YYYY-MM-DD format');
      }
      if (normalizedStartDate > normalizedEndDate) {
        throw new Error('startDate cannot be after endDate');
      }
    }

    const response = yield call(
      getConsumptionService,
      orgNodeId,
      subscriptionId,
      normalizedStartDate,
      normalizedEndDate,
    );
    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    yield put({ type: types.LOAD_CONSUMPTION_DATA_SUCCESS, data: response.json });
  } catch (error) {
    logError('Error in loadConsumptionDataSaga', {
      error: error.message,
      errorCode: error.errorCode,
      orgId,
      subscriptionId,
    });
    yield put({ type: types.LOAD_CONSUMPTION_DATA_ERROR, error: error.message });
  }
}
